// VisitItem.tsx
import { Button, HStack, Spinner, Td, Text, Tr, VStack } from '@chakra-ui/react';
import { ChevronDownIcon, Icon } from '@chakra-ui/icons';
import { format, isPast, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { CancelVisitModal } from 'screens/Class/components/CancelVisitModal'; // Assurez-vous que le chemin est correct
import { PickaspotClass } from 'types/class';
import { ClientVisit } from 'types/client'; // Assurez-vous que le chemin est correct
import { isVisitLateCancel } from 'utils/functions'; // Assurez-vous que le chemin est correct
import { HTTP } from 'utils/http';

interface VisitItemProps {
  index: number;
  visit: ClientVisit;
  coachName?: string;
  locationName?: string;
  isMobile: boolean;
}

const VisitsHistoryItem: React.FC<VisitItemProps> = ({ index, visit, coachName, locationName, isMobile }) => {
  const [isCancelled, setIsCancelled] = useState(visit.AppointmentStatus === 'Cancelled');
  const [loadingSpot, setLoadingSpot] = useState(false);
  const [memberSpot, setMemberSpot] = useState<number | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const handleToggleDetails = () => setShowDetails(!showDetails);

  const rotateIcon = showDetails ? 'rotate(180deg)' : 'rotate(0)';

  const handleCancelSuccess = () => {
    setIsCancelled(true);
  };

  const handleDiscoverSpot = async () => {
    setLoadingSpot(true);
    try {
      const reservation = await HTTP.get<PickaspotClass['reservations'][number]>(
        `/class/${visit.ClassId}/reservation`,
        {
          SiteId: visit.SiteId,
          VisitId: visit.Id,
        },
      ).send();
      setMemberSpot(reservation?.spot?.spotNumber || null);
    } catch (error) {
      // Gérez l'erreur si nécessaire
    } finally {
      setLoadingSpot(false);
    }
  };

  useEffect(() => {
    if (visit.AppointmentStatus === 'Cancelled') {
      setIsCancelled(true);
    }
  }, [visit.AppointmentStatus]);

  return (
    <>
      <Tr key={`${visit.AppointmentStatus}_${index}`}>
        <Td width={isMobile ? '33%' : 'auto'} padding={isMobile ? '5px' : 'initial'}>
          <Text textTransform={'uppercase'} fontWeight="semibold">
            {visit.Name}
          </Text>
        </Td>
        <Td width={isMobile ? '33%' : 'auto'} padding={isMobile ? '5px' : 'initial'}>
          <HStack justifyContent={'space-between'}>
            <Text align="left">{format(parseISO(visit.StartDateTime), 'dd/MM/yyyy - HH:mm')}</Text>
          </HStack>
        </Td>
        <Td
          width={isMobile ? '100%' : 'auto'}
          padding={isMobile ? '5px' : 'initial'}
          display={isMobile ? 'flex' : 'table-cell'}
        >
          <Text align="left">{locationName ?? '-'}</Text>
          {isMobile && (
            <Button size="md" onClick={handleToggleDetails} variant="ghost">
              <Icon as={ChevronDownIcon} transition="transform .2s ease-in-out" transform={rotateIcon} />
            </Button>
          )}
        </Td>
        {!isMobile && (
          <>
            <Td>
              {memberSpot ? (
                <Text fontSize="xs" align="left" marginLeft="3px">
                  Spot : {memberSpot}
                </Text>
              ) : visit.ClassId !== 0 ? (
                <Button
                  sx={{
                    padding: 0,
                    fontWeight: 400,
                    textDecoration: 'underline',
                    marginTop: '1px',
                    height: 'auto',
                    marginLeft: '3px',
                  }}
                  onClick={handleDiscoverSpot}
                  variant="underlined"
                  fontSize="xs"
                  isLoading={loadingSpot}
                  spinner={<Spinner size="xs" />}
                >
                  Mon Spot
                </Button>
              ) : (
                '-'
              )}
            </Td>
            <Td>
              <Text align="left">{coachName ?? '-'}</Text>
            </Td>
            <Td>
              {!isPast(new Date(visit.StartDateTime)) ? (
                isCancelled ? (
                  <Text align="center" fontWeight="700" color="red.500" textTransform="uppercase" mt={4} fontSize="sm">
                    Annulée
                  </Text>
                ) : (
                  <CancelVisitModal visit={visit} onCancelSuccess={handleCancelSuccess}>
                    <Button
                      textTransform="uppercase"
                      sx={{ background: 'transparent', marginTop: 4, color: 'black', textDecoration: 'underline' }}
                      size="sm"
                    >
                      {isVisitLateCancel(visit) ? 'Annulation tardive' : 'Annuler la séance'}
                    </Button>
                  </CancelVisitModal>
                )
              ) : visit.ClassId !== 0 ? (
                visit.AppointmentStatus === 'Cancelled' ? (
                  'Annulée'
                ) : !visit.Missed && visit.SignedIn ? (
                  'Show'
                ) : (
                  'No Show'
                )
              ) : (
                '-'
              )}
            </Td>
          </>
        )}
      </Tr>
      {isMobile && showDetails && (
        <Tr>
          <Td colSpan={6}>
            <VStack align="start" spacing={2}>
              <Text>
                <strong>Spot :</strong>
                {memberSpot ? (
                  <Text fontSize="xs" align="left" marginLeft="3px">
                    Spot : {memberSpot}
                  </Text>
                ) : (
                  <Button
                    sx={{
                      padding: 0,
                      fontWeight: 400,
                      textDecoration: 'underline',
                      marginTop: '1px',
                      height: 'auto',
                      marginLeft: '3px',
                    }}
                    onClick={handleDiscoverSpot}
                    variant="underlined"
                    fontSize="xs"
                    isLoading={loadingSpot}
                    spinner={<Spinner size="xs" />}
                  >
                    Mon Spot
                  </Button>
                )}
              </Text>
              <Text>
                <strong>Coach :</strong> {coachName ?? '-'}
              </Text>
              <Text>
                <strong>Action / Statut :</strong>
                {!isPast(new Date(visit.StartDateTime)) ? (
                  isCancelled ? (
                    <Text
                      align="center"
                      fontWeight="700"
                      color="red.500"
                      textTransform="uppercase"
                      mt={4}
                      fontSize="sm"
                    >
                      Annulée
                    </Text>
                  ) : (
                    <CancelVisitModal visit={visit} onCancelSuccess={handleCancelSuccess}>
                      <Button
                        textTransform="uppercase"
                        sx={{ background: 'transparent', marginTop: 4, color: 'black', textDecoration: 'underline' }}
                        size="sm"
                      >
                        {isVisitLateCancel(visit) ? 'Annulation tardive' : 'Annuler la séance'}
                      </Button>
                    </CancelVisitModal>
                  )
                ) : visit.AppointmentStatus === 'Cancelled' ? (
                  ' Annulée'
                ) : !visit.Missed && visit.SignedIn ? (
                  ' Show'
                ) : (
                  ' No Show'
                )}
              </Text>
            </VStack>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default VisitsHistoryItem;
